import { Alert, Box, Grid, Container, CircularProgress, Typography } from "@mui/material";
import logo from "../assets/logo-black/logo-black.svg";

import {
  Authenticator,
  CheckboxField,
  View,
  useAuthenticator,
  useTheme,
  Text,
} from "@aws-amplify/ui-react";
import {
  signIn,
  confirmSignUp,
  SignInInput,
  signUp,
  SignUpInput,
  fetchAuthSession,
  signOut,
} from "aws-amplify/auth";
import axios from "axios";
import { getUserAPI } from "api/user.api";
import { I18n } from "aws-amplify/utils";
import { translations } from "@aws-amplify/ui-react";
import { AuthContext } from "providers/userProvider";
import { useContext, useEffect, useState } from "react";
import { AuthData } from "types/auth";
import { isValidEmail, EMAIL_VALIDATION_ERROR_MESSAGE } from "utils/validate-email";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  FundDashboardPage,
  MyFundsPageUrl,
  UserSettingsPage,
} from "constants/pages";
I18n.putVocabularies(translations);
I18n.setLanguage("en");
/** Set after email OTP so the profile gate can show signup-specific copy (not sign-in). */
const SIGNUP_PROFILE_GATE_SESSION_KEY = "www-auth-profile-gate-signup-flow";
const ACCOUNT_ACCESS_BLOCKED_SUBSTRING = "Your account has been deactivated";

async function assertUserAllowedAfterCognitoSignIn(): Promise<void> {
  const { tokens } = await fetchAuthSession({ forceRefresh: true });
  const sub = tokens?.idToken?.payload?.sub as string | undefined;
  if (!sub) {
    return;
  }
  try {
    await getUserAPI(sub);
  } catch (e) {
    if (
      axios.isAxiosError(e) &&
      e.response?.status === 403 &&
      typeof (e.response?.data as { message?: string })?.message === "string" &&
      (e.response.data as { message: string }).message.includes(
        ACCOUNT_ACCESS_BLOCKED_SUBSTRING
      )
    ) {
      await signOut();
      throw new Error((e.response.data as { message: string }).message);
    }
    // New accounts often have no DB row yet, or tokens may lag right after OTP; do not block Cognito.
    // userProvider will load users/sub again once the session is stable.
    return;
  }
}

I18n.putVocabulariesForLanguage("en", {
  "Password must have lower case letters":
    "Password must have at least one lower case letter",
  "Password must have upper case letters":
    "Password must have at least one upper case letter",
  "Password must have numbers": "Password must have at least one number",
  "Password must have special characters":
    "Password must have at least one special character",
  "An account with the given email already exists.":
    "An account with the given email already exists. Please try to sign in, or click the Forgot Your Password link to re-set your password",
  "We Emailed You": "We emailed you",
  "Your code is on the way. To log in, enter the code we emailed to":
    "We want to verify you have access to the email you entered. Please check",
  "It may take a minute to arrive":
    "for the code we sent you, and enter it below. Please note it may take a few minutes to arrive, or may end up in your spam folder",
  "Confirm TOTP Code": "Confirm two-Factor Authentication Code",
});
export const AuthPage = () => {
  const [blockedSignInMessage, setBlockedSignInMessage] = useState<
    string | null
  >(null);
  useEffect(() => {
    const m = sessionStorage.getItem("SIGN_IN_BLOCKED_MESSAGE");
    if (m) {
      setBlockedSignInMessage(m);
      sessionStorage.removeItem("SIGN_IN_BLOCKED_MESSAGE");
    }
  }, []);

  const passwordSettings = {
    minLength: 8,
    requireLowercase: true,
    requireUppercase: true,
    requireNumbers: true,
    requireSpecialCharacters: true,
  };
  const formFields = {
    signIn: {
      username: {
        placeholder: "Enter your email",
        isRequired: true,
        label: "Email:",
      },
    },
    signUp: {
      username: {
        placeholder: "Enter your email",
        isRequired: true,
        label: "Email:",
      },
    },
    forgotPassword: {
      username: {
        placeholder: "Enter Your Email Here",
        isRequired: true,
        label: "Email:",
      },
    },
  };
  const components = {
    SignUp: {
      FormFields() {
        const { validationErrors } = useAuthenticator();
        return (
          <>
            {/* Re-use default `Authenticator.SignUp.FormFields` */}
            <Authenticator.SignUp.FormFields />

            {/* Append & require Terms & Conditions field to sign up  */}
            <CheckboxField
              value="yes"
              errorMessage={validationErrors.acknowledgement as any}
              hasError={!!validationErrors.acknowledgement}
              name="acknowledgement"
              isRequired={false}
              label={
                <>
                  Accepting{" "}
                  <a href="/agreement-policies" target="_blank">
                    program guidelines
                  </a>{" "}
                  and{" "}
                  <a href="/policy" target="_blank">
                    privacy policy
                  </a>{" "}
                </>
              }
            />
          </>
        );
      },
      Footer() {
        const { tokens } = useTheme();

        return (
          <View
            textAlign="center"
            paddingInlineEnd={tokens.space.small}
            paddingInlineStart={tokens.space.small}
          >
            <Text color={tokens.colors.neutral[80]}>
              We're using an external service to keep your email and password
              secure
            </Text>
          </View>
        );
      },
    },
  };
  const services = {
    async validateCustomSignUp(formData: any) {
      const errors: Record<string, string> = {};

      if ("username" in formData && !isValidEmail(formData.username)) {
        errors.username = EMAIL_VALIDATION_ERROR_MESSAGE;
      }

      if (
        ("acknowledgement" in formData &&
          formData?.acknowledgement === undefined) ||
        (["username", "password", "confirm_password"].every(
          (t) => t in formData
        ) &&
          !("acknowledgement" in formData))
      ) {
        errors.acknowledgement = "You must agree to the Terms & Conditions";
      }

      if (Object.keys(errors).length > 0) {
        return errors;
      }
    },
    async handleSignUp(input: SignUpInput) {
      sessionStorage.removeItem(SIGNUP_PROFILE_GATE_SESSION_KEY);
      console.info(
        `Create Account attempted for email: ${input.username}`,
      );
      return signUp(input);
    },
    async handleSignIn(formData: { username: string; password: string }) {
      sessionStorage.removeItem(SIGNUP_PROFILE_GATE_SESSION_KEY);
      const { username, password } = formData;
      if (!isValidEmail(username)) {
        throw new Error(EMAIL_VALIDATION_ERROR_MESSAGE);
      }
      const result = await signIn({ username, password });
      if (result.nextStep.signInStep === "CONFIRM_SIGN_UP") {
        sessionStorage.setItem("username", username);
        sessionStorage.setItem("password", password);
      }

      if (result.nextStep.signInStep === "DONE") {
        await assertUserAllowedAfterCognitoSignIn();
      }

      return result;
    },
    async handleConfirmSignUp(formData: {
      username: string;
      confirmationCode: string;
    }) {
      const { username, confirmationCode } = formData;
      const result = await confirmSignUp({
        username,
        confirmationCode,
        options: { autoSignIn: true },
      });
      if (
        result.isSignUpComplete &&
        result.nextStep.signUpStep === "COMPLETE_AUTO_SIGN_IN"
      ) {
        sessionStorage.setItem(SIGNUP_PROFILE_GATE_SESSION_KEY, "1");
        return result;
      } else {
        const data = {
          username: sessionStorage.getItem("username"),
          password: sessionStorage.getItem("password"),
        } as SignInInput;
        sessionStorage.removeItem("username");
        sessionStorage.removeItem("password");
        const signInResult = await signIn(data);
        if (signInResult.nextStep.signInStep === "DONE") {
          await assertUserAllowedAfterCognitoSignIn();
        }
        sessionStorage.setItem(SIGNUP_PROFILE_GATE_SESSION_KEY, "1");
        window.location.reload();
      }
    },
  };
  const navigate = useNavigate();
  const { toSignUp, authStatus } = useAuthenticator((context) => [
    context.route,
    context.authStatus,
  ]);
  const [params] = useSearchParams();
  const { userData } = useContext<AuthData>(AuthContext);
  const showProfileLoadingGate =
    authStatus === "authenticated" &&
    !userData?.user &&
    Boolean(userData?.sub);
  const profileGateMessage =
    typeof sessionStorage !== "undefined" &&
    sessionStorage.getItem(SIGNUP_PROFILE_GATE_SESSION_KEY) === "1"
      ? "Continuing sign-up…"
      : "Signing you in...";
  useEffect(() => {
    if (params && params.get('createAccount') === "true") {
      toSignUp()
    }
    if (userData?.user) {
      const loggedIn = localStorage.getItem("loggedIn");
      if (loggedIn === "true") {
        const fundCount = userData.user.fundAccounts?.length ?? 0;
        if (fundCount === 1) {
          navigate(FundDashboardPage);
        } else if (fundCount > 1) {
          navigate(MyFundsPageUrl);
        } else {
          navigate(UserSettingsPage);
        }
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, userData, params]);

  return (
    <>
      <Box sx={{ display: "flex", minHeight: "100vh" }}>
        <Container>
          <Grid container direction="row" alignItems="center">
            <Grid item xs={12} sm={3} md={3} lg={3}></Grid>
            <Grid item xs={12} sm={8} md={6} lg={6}>
              <Grid
                container
                direction="row"
                sx={{ my: 4, backgroundColor: "white" }}
                rowSpacing={2}
              >
                <Grid item xs={12} sm={12} md={12} lg={12} textAlign={"center"}>
                  <Box component="img" alt="Logo" src={logo} />
                </Grid>
                <Grid item xs={12} sm={12} md={12} lg={12} paddingBottom={6}>
                  {blockedSignInMessage ? (
                    <Alert
                      severity="error"
                      onClose={() => setBlockedSignInMessage(null)}
                      sx={{ mb: 2, textAlign: "left" }}
                    >
                      {blockedSignInMessage}
                    </Alert>
                  ) : null}
                  {showProfileLoadingGate ? (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 2,
                        py: 4,
                      }}
                    >
                      <CircularProgress />
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        textAlign="center"
                      >
                        {profileGateMessage}
                      </Typography>
                    </Box>
                  ) : (
                    <Authenticator
                      passwordSettings={passwordSettings}
                      services={services as any}
                      formFields={formFields}
                      components={components}
                    />
                  )}
                </Grid>
                <Grid item xs={12} sm={12} md={12} lg={12}></Grid>
              </Grid>
            </Grid>
            <Grid item xs={12} sm={3} md={3} lg={3}></Grid>
          </Grid>
        </Container>
      </Box>
    </>
  );
};
